import React, { useState } from 'react';
import {
    Box,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import Meta from '../../types/negocio/Meta';
import BasicModal from './BasicModal';
import SelectColaborador from '../SelectColaborador';

interface Props {
    open: boolean;
    onClose: () => void;
    onSave?: (data: Meta) => void;
    meta: Meta;
    modoEdicao?: boolean;
    setMeta?: React.Dispatch<React.SetStateAction<Meta>>;
    onEditClick?: () => void;
    isNovaMeta?: boolean;
}

const MetaModal: React.FC<Props> = ({
    open,
    onClose,
    onSave,
    meta,
    modoEdicao = false,
    setMeta,
    onEditClick,
    isNovaMeta = false
}) => {
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const [saving, setSaving] = useState(false);

    const camposObrigatorios = ['meta', 'tipo', 'responsavelGuid'];
    const tiposDisponiveis = ['Valor', 'Número', 'Percentual'];
    const meses = [
        { key: 'janeiro', label: 'Janeiro' },
        { key: 'fevereiro', label: 'Fevereiro' },
        { key: 'marco', label: 'Março' },
        { key: 'abril', label: 'Abril' },
        { key: 'maio', label: 'Maio' },
        { key: 'junho', label: 'Junho' },
        { key: 'julho', label: 'Julho' },
        { key: 'agosto', label: 'Agosto' },
        { key: 'setembro', label: 'Setembro' },
        { key: 'outubro', label: 'Outubro' },
        { key: 'novembro', label: 'Novembro' },
        { key: 'dezembro', label: 'Dezembro' }
    ];

    // Validar campos obrigatórios
    const validarCampos = (): boolean => {
        const novosErros: Record<string, boolean> = {};
        let temErro = false;

        for (const campo of camposObrigatorios) {
            if (!meta[campo as keyof Meta] || (meta[campo as keyof Meta] as string).trim() === '') {
                novosErros[campo] = true;
                temErro = true;
            }
        }

        setErrors(novosErros);
        return !temErro;
    };

    const handleSave = async () => {
        if (validarCampos() && onSave) {
            // Calcular totais antes de salvar
            const metaAtualizada = calcularTotais(meta);
            setSaving(true);
            try {
                await onSave(metaAtualizada);
            } finally {
                setSaving(false);
            }
        }
    };

    const calcularTotais = (metaAtual: Meta): Meta => {
        let totalOrcado = 0;
        let totalRealizado = 0;

        for (const mes of meses) {
            const orcadoKey = `${mes.key}Orcado` as keyof Meta;
            const realizadoKey = `${mes.key}Realizado` as keyof Meta;
            
            const orcado = (metaAtual[orcadoKey] as number) || 0;
            const realizado = (metaAtual[realizadoKey] as number) || 0;
            
            totalOrcado += orcado;
            totalRealizado += realizado;
        }

        return {
            ...metaAtual,
            totalOrcado,
            totalRealizado
        };
    };

    const handleChange = (campo: string, valor: any) => {
        if (setMeta) {
            let novoValor = valor;
            if (campo.includes('Orcado') || campo.includes('Realizado')) {
                novoValor = valor === '' ? 0 : Number(valor);
            }
            
            setMeta(prev => ({
                ...prev,
                [campo]: novoValor
            }));
            
            // Limpar erro do campo se estava com erro
            if (errors[campo]) {
                setErrors(prev => ({ ...prev, [campo]: false }));
            }
        }
    };

    const formatarValor = (valor?: number, tipo?: string) => {
        if (!valor) return '';
        
        if (tipo === 'Valor') {
            return valor.toLocaleString('pt-BR', { 
                style: 'currency', 
                currency: 'BRL' 
            });
        } else if (tipo === 'Percentual') {
            return `${valor}%`;
        }
        return valor.toString();
    };

    const getInputAdornment = (tipo: string) => {
        if (tipo === 'Valor') {
            return { startAdornment: <InputAdornment position="start">R$</InputAdornment> };
        } else if (tipo === 'Percentual') {
            return { endAdornment: <InputAdornment position="end">%</InputAdornment> };
        }
        return {};
    };

    const getModalTitle = () => {
        if (isNovaMeta) return "Nova Meta";
        if (modoEdicao) return "Editar Meta";
        return "Visualizar Meta";
    };

    return (
        <BasicModal
            open={open}
            onClose={onClose}
            saveButton={modoEdicao}
            onSave={modoEdicao ? handleSave : undefined}
            saveLoading={saving}
            onEdit={modoEdicao ? undefined : onEditClick}
            title={getModalTitle()}
            sizeContent="large"
            tela='metas'
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: '800px' }}>
                
                {/* Campo Meta */}
                <TextField
                    fullWidth
                    label="Meta"
                    variant="outlined"
                    color="secondary"
                    value={meta.meta || ''}
                    onChange={(e) => handleChange('meta', e.target.value)}
                    error={errors.meta}
                    helperText={errors.meta ? 'Campo obrigatório' : ''}
                    disabled={!modoEdicao}
                />

                {/* Campo Tipo */}
                <FormControl fullWidth color="secondary" error={errors.tipo}>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                        value={meta.tipo || ''}
                        onChange={(e) => handleChange('tipo', e.target.value)}
                        disabled={!modoEdicao}
                        label="Tipo"
                    >
                        {tiposDisponiveis.map((tipo) => (
                            <MenuItem key={tipo} value={tipo}>
                                {tipo}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.tipo && (
                        <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                            Campo obrigatório
                        </Typography>
                    )}
                </FormControl>

                {/* Campo Responsável */}
                <Box>
                    <SelectColaborador
                        value={meta.responsavelGuid || ''}
                        onChange={(colaborador) => handleChange('responsavelGuid', colaborador?.guid ?? '')}
                        disabled={!modoEdicao}
                    />
                    {errors.responsavelGuid && (
                        <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                            Campo obrigatório
                        </Typography>
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Tabela de Meses */}
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Planejamento Mensal
                </Typography>

                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Mês</strong></TableCell>
                                <TableCell align="center"><strong>Orçado</strong></TableCell>
                                <TableCell align="center"><strong>Realizado</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {meses.map((mes) => {
                                const orcadoKey = `${mes.key}Orcado` as keyof Meta;
                                const realizadoKey = `${mes.key}Realizado` as keyof Meta;

                                return (
                                    <TableRow key={mes.key}>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {mes.label}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={meta[orcadoKey] || ''}
                                                onChange={(e) => handleChange(orcadoKey, e.target.value)}
                                                disabled={!modoEdicao}
                                                sx={{ width: '120px' }}
                                                {...getInputAdornment(meta.tipo || '')}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={meta[realizadoKey] || ''}
                                                onChange={(e) => handleChange(realizadoKey, e.target.value)}
                                                disabled={!modoEdicao}
                                                sx={{ width: '120px' }}
                                                {...getInputAdornment(meta.tipo || '')}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}

                            {/* Linha de Total */}
                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                <TableCell>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Total
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="body2" fontWeight="bold">
                                        {formatarValor(meta.totalOrcado, meta.tipo)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="body2" fontWeight="bold">
                                        {formatarValor(meta.totalRealizado, meta.tipo)}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </BasicModal>
    );
};

export default MetaModal;